import * as React from 'react';

import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { Form, FormikProps, withFormik } from 'formik';
import { withSnackbar, withSnackbarProps } from 'notistack';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../../../actions/account.actions';
import { accountConstants } from '../../../constants/account.constants';
import { validate } from '../../../helpers/validation';
import { accountRoutesConst as routes } from '../../../routes/account.routes';
import { LoadingButton } from '../../../ui/LoadingButton';
import PinCodeTextField from '../../../ui/TextField/PinCodeTextField';
import AuthLayout from '../Common/Layout';
import LoginTitle from './LoginTitle';

interface IFormValues {
  email: string;
  code: string;
}

enum InvalidPinMessage {
  AccountLocked = 'User_is_locked_out',
  AccountPinExpired = 'Expired_pin_login',
}

const styles = (theme: Theme) =>
  createStyles({
    formFields: {
      // remove chrome autofill color
      '& input:-webkit-autofill': {
        boxShadow: '0 0 0 30px white inset !important',
      },
    },
    textField: {
      // prevent jumping screen
      minHeight: '68px',
    },
    languageBarNoMargin: {
      margin: 0,
    },
    extras: {
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
      },
    },
    linkNoPadding: {
      padding: `${theme.spacing(1)}px 0`,
      textTransform: 'none',
    },
    resendPinWrapper: {
      margin: 0,
    },
  });

const Title = () => (
  <Translate>
    {(translate) => (
      <LoginTitle title={translate('Please_check_email_for_pin') as string}>
        <Typography
          variant="subtitle1"
          style={{
            color: '#ffffff',
            letterSpacing: 'normal',
            maxWidth: '250px',
          }}
        >
          {translate('Havent_received_in_5_minutes')}
        </Typography>
      </LoginTitle>
    )}
  </Translate>
);

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = (initialState, ownProps) => {
  return (state: AppState, ownProps) => {
    const { account } = state;
    return {
      account,
      translate: getTranslate(state.locale),
    };
  };
};

type Props = WithStyles<typeof styles> &
  withSnackbarProps &
  MappedState & { dispatch: ThunkDispatch };
const LoginPin = compose<Props, {}>(
  setDisplayName('LoginPin'),
  withStyles(styles, {
    name: 'FdLoginPin',
    withTheme: true,
  }),
  withSnackbar,
  connect(mapStateToPropsFactory),
  withFormik<Props, IFormValues>({
    displayName: 'LoginPinForm',
    mapPropsToValues: (props) => {
      return {
        email: props.account.Email as string,
        code: '',
      };
    },
    handleSubmit: async (values, { props, setSubmitting, setFieldError }) => {
      const { dispatch } = props;
      try {
        await dispatch(accountActions.loginWithPin(values.email, values.code));
      } catch (error) {
        if (error.message === InvalidPinMessage.AccountLocked) {
          setFieldError('code', props.translate('User_is_locked_out_pin') as string);
          props.enqueueSnackbar(props.translate('User_is_locked_out_pin'), {
            variant: 'error',
          });
        } else if (error.message === InvalidPinMessage.AccountPinExpired) {
          setFieldError('code', props.translate('Expired_pin') as string);
          props.enqueueSnackbar(props.translate('Expired_pin'), {
            variant: 'error',
          });
        } else {
          setFieldError('code', props.translate('Invalid_pin') as string);
          props.enqueueSnackbar(props.translate('Invalid_pin'), {
            variant: 'error',
          });
        }
        setSubmitting(false);
      }
    },
    validate: (values) => {
      return validate.required(values.code);
    },
  })
)(
  class extends React.Component<Props & FormikProps<IFormValues>, {}> {
    private inputRef = React.createRef<HTMLInputElement>();

    public componentDidMount() {
      this.focus();
    }

    public render() {
      const { classes, translate, isSubmitting, touched, errors, account, values } = this.props;

      if (!account.Email) {
        return <Redirect to={routes.Login} />;
      }

      return (
        <AuthLayout title={Title}>
          <Grid container alignItems="stretch" direction="column" wrap="nowrap" spacing={2}>
            <Grid item>
              <Form>
                <Grid container spacing={0} className={classes.formFields}>
                  <Grid item xs={12} lg={11}>
                    <TextField
                      name="email"
                      label={translate('Email')}
                      placeholder={translate('Email') as string}
                      type="email"
                      autoComplete="email"
                      fullWidth
                      inputProps={{
                        'data-fd': 'email',
                      }}
                      className={classes.textField}
                      disabled={true}
                      value={values.email}
                    />
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <PinCodeTextField
                      key="pin_input"
                      id="pin_code_input"
                      autoFocus
                      fullWidth
                      onFulfill={this.handleSubmit}
                      error={touched.code && !!errors.code}
                      helperText={touched.code && errors.code}
                    />
                  </Grid>
                </Grid>
              </Form>
            </Grid>

            <Grid item container direction="column" className={classes.extras}>
              <Hidden xsDown implementation="js">
                <Grid item>
                  <Typography variant="subtitle1">
                    <Translate id="Havent_received_pin_code" />
                  </Typography>
                </Grid>
              </Hidden>
              <Grid item>
                <LoadingButton
                  buttonProps={{
                    color: 'primary',
                    onClick: this.handleResendPinClick,
                    'data-fd': 'resend-pin',
                    className: classes.linkNoPadding,
                  }}
                  wrapperClassName={classes.resendPinWrapper}
                  loadingResources={[accountConstants.LOGIN_REQUEST_PIN]}
                  disabled={isSubmitting}
                >
                  <Translate id="Resend_pin" />
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </AuthLayout>
      );
    }

    public focus = () => {
      if (this.inputRef.current) {
        this.inputRef.current.focus();
      }
    };

    private handleSubmit = (code: string) => {
      this.props.setFieldValue('code', code);
      setTimeout(this.props.submitForm, 0);
    };

    private handleResendPinClick = async () => {
      const { dispatch, account, enqueueSnackbar, setSubmitting, translate } = this.props;
      setSubmitting(true);
      await dispatch(accountActions.loginRequestPin(account.Email as string));
      enqueueSnackbar(translate('Pin_sent'), { variant: 'success' });
      setSubmitting(false);
      setTimeout(this.focus, 0);
    };
  }
);

export default LoginPin;
